import { parseDecimal } from '@klopotun/domain';
import { z } from 'zod';
import { amountMinorSchema, convertedAmountMinorSchema, civilDateSchema, currencyCodeSchema, isoTimestampSchema, optionalTextSchema, uuidSchema, } from './common.js';
export const occurrenceStatusSchema = z.enum(['planned', 'paid', 'paused', 'cancelled']);
export const voidReasonSchema = z.enum([
    'payment_cancelled',
    'schedule_changed',
    'manual',
    'bank_missed',
]);
/**
 * The answer to the "just this once or from now on" question of design note D2, transition 2.
 * A variable payment is never asked it - there is no expected amount to carry forward - which is
 * why the field carries a default rather than being required: a body that never asked the question
 * has nothing to answer.
 */
export const applyAmountToSchema = z.enum(['once', 'always']);
const isPositiveDecimalString = (value) => {
    try {
        return parseDecimal(value).units > 0n;
    }
    catch {
        return false;
    }
};
/**
 * A numeric(20,10) column reaches Node as a decimal string and every rate calculation in
 * packages/domain works on that string: turning the rate into a number loses money on each
 * conversion (design note C0). The positivity rule mirrors the occurrence_fx_rate_ck CHECK.
 */
const fxRateSchema = z.string().refine(isPositiveDecimalString, 'must be a positive decimal rate');
const occurrenceShape = {
    id: uuidSchema,
    paymentId: uuidSchema,
    periodStart: civilDateSchema,
    periodEnd: civilDateSchema,
    dueDate: civilDateSchema,
    /**
     * Empty only for a payment with a variable amount (design note C5a). No CHECK can hold that
     * rule and neither can this schema: the amount mode lives in the payment row, one table away —
     * design note C12 leaves this single invariant to the service and to its test.
     */
    expectedAmountMinor: amountMinorSchema.nullable(),
    expectedCurrency: currencyCodeSchema,
    status: occurrenceStatusSchema,
    voidReason: voidReasonSchema.nullable(),
    paidOn: civilDateSchema.nullable(),
    paidAt: isoTimestampSchema.nullable(),
    paidAmountMinor: amountMinorSchema.nullable(),
    paidCurrency: currencyCodeSchema.nullable(),
    fxBaseCurrency: currencyCodeSchema.nullable(),
    fxRate: fxRateSchema.nullable(),
    fxRateDate: civilDateSchema.nullable(),
    fxSource: optionalTextSchema,
    baseAmountMinor: convertedAmountMinorSchema.nullable(),
    note: optionalTextSchema,
    createdAt: isoTimestampSchema,
    updatedAt: isoTimestampSchema,
};
/**
 * The raw shape stays separate from the schema below: the consistency rules are attached with
 * .refine, and a refined schema can no longer be spread or extended.
 */
const occurrenceFieldsSchema = z.object(occurrenceShape);
const hasPaymentFactsWhenPaid = (occurrence) => {
    if (occurrence.status !== 'paid') {
        return true;
    }
    const hasDate = occurrence.paidOn !== null;
    const hasAmount = hasDate && occurrence.paidAmountMinor !== null;
    const hasCurrency = hasAmount && occurrence.paidCurrency !== null;
    return hasCurrency;
};
const hasVoidReasonExactlyWhenCancelled = (occurrence) => (occurrence.status === 'cancelled') === (occurrence.voidReason !== null);
/**
 * Zero padded YYYY-MM-DD compares correctly as plain text; the comparison below breaks the moment
 * civilDateSchema stops enforcing that shape.
 */
const hasPeriodEndAfterStart = (occurrence) => occurrence.periodEnd > occurrence.periodStart;
export const occurrenceSchema = occurrenceFieldsSchema
    .refine(hasPaymentFactsWhenPaid, {
    message: 'a paid occurrence carries the date, the amount and the currency together',
    path: ['status'],
})
    .refine(hasVoidReasonExactlyWhenCancelled, {
    message: 'a void reason belongs to a cancelled occurrence and to nothing else',
    path: ['voidReason'],
})
    .refine(hasPeriodEndAfterStart, {
    message: 'the period has to end after it starts',
    path: ['periodEnd'],
});
export const occurrenceListSchema = z.array(occurrenceSchema);
/**
 * Every filter is optional and they narrow the listing together. No parameters at all is not
 * "every occurrence": design note C12 makes the default the planned ones from today to the
 * materialisation horizon, and the service is what spells that out.
 */
const listOccurrencesQueryFieldsSchema = z.object({
    status: occurrenceStatusSchema.optional(),
    paymentId: uuidSchema.optional(),
    from: civilDateSchema.optional(),
    to: civilDateSchema.optional(),
});
const hasRangeEndNotBeforeStart = (query) => query.from === undefined || query.to === undefined || query.to >= query.from;
export const listOccurrencesQuerySchema = listOccurrencesQueryFieldsSchema.refine(hasRangeEndNotBeforeStart, {
    message: 'the range cannot end earlier than it starts',
    path: ['to'],
});
export const payOccurrenceBodySchema = z.object({
    paidOn: civilDateSchema,
    paidAmountMinor: amountMinorSchema,
    paidCurrency: currencyCodeSchema,
    applyAmountTo: applyAmountToSchema.default('once'),
});
//# sourceMappingURL=occurrence.js.map